import sumBy from 'lodash/sumBy';

import { getScreenHeight } from '../mobile-library/contentFrameInternals/getScreenHeight';
import type { MobileContentFrameWindow } from '../mobile-library/contentFrameInternals/types';
import type { WebviewDocumentChunk } from '../types/chunkedDocuments';

declare let window: MobileContentFrameWindow;
export function getWordsScrolled(scrollDepth: number, totalWordsInDocument?: number) {
  return totalWordsInDocument ? totalWordsInDocument * scrollDepth : 0;
}

export function getWordsScrolledDelta(wordsScrolled: number, startScrollValue: number) {
  return wordsScrolled - startScrollValue;
}

export function getIsScrolledToBottom(scrollDepth: number) {
  return scrollDepth === 1;
}

type VerticalProgressParams = {
  contentElement: HTMLElement | undefined;
  viewportHeight: number;
  centeredElement?: HTMLElement | Range | DOMRect; // Optional override for content top position
  bottomOffset?: number; // Optional offset in pixels to account for bottom element differences in mobile and web
  startPercentageInViewport?: number; // Optional percentage to start the progress calculation from
};

/**
 * Calculates how far the element has been scrolled as a value between 0 and 1.
 *
 * This function accepts either a top value or falls back to the top of the content element.
 */
function calculateVerticalScrollPercentOfElement({
  contentElement,
  viewportHeight,
  centeredElement,
  bottomOffset = 0,
  startPercentageInViewport = 0.5,
}: VerticalProgressParams): number {
  if (!contentElement) {
    return 0;
  }

  const contentRect = contentElement.getBoundingClientRect();
  const centeredElementRect = centeredElement instanceof DOMRect ? centeredElement : centeredElement?.getBoundingClientRect();
  const viewPortOffset = viewportHeight * startPercentageInViewport;

   // Calculate how much content has been scrolled above the threshold
  //  We use the headerTop to start scrolling progress immediately when the user
  //  scrolls from the beginning of the document.
  const contentTop = centeredElement ? contentRect.top - (centeredElementRect?.top ?? 0) : contentRect.top;

  const totalAboveViewport = Math.max(0, -contentTop + viewPortOffset);

  // This accounts for the bottom elements that take up space in the viewport
  // They are defined as absolute pixels and thus we cannot work with relative viewportHeight here.
  const totalScrollableHeight = contentRect.height - bottomOffset + viewPortOffset;

  // The px values cancel out, so we receive a device independent value
  const progress = totalAboveViewport / totalScrollableHeight;

  return Math.min(1, Math.max(0, progress));
}

export function getChunkScrollDepthInWeb(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement) {
  // We use the window and bottom element height to calculate an offset
  const bottomOffset = window.innerHeight + 205;

  return calculateVerticalScrollPercentOfElement({
    contentElement: currentChunkElement,
    viewportHeight: window.innerHeight,
    bottomOffset,
    startPercentageInViewport: 0.3,
    centeredElement: serializedPositionElement,
  });
}

export function getChunkScrollDepthInMobile(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement | Range | DOMRect) {
  const bottomOffset = getScreenHeight(window) - 520;

  return calculateVerticalScrollPercentOfElement({
    contentElement: currentChunkElement,
    // On mobile innerHeight can change while the content is loading
    viewportHeight: getScreenHeight(window),
    bottomOffset,
    startPercentageInViewport: 0.2,
    centeredElement: serializedPositionElement,
  });
}

function calculateHorizontalScrollPercentOfElement({ containerElement, targetElement }: {containerElement?: HTMLElement; targetElement?: HTMLElement | Range | DOMRect;}) {
  if (!containerElement) {
    return 0;
  }

  if (!targetElement) {
    const scrollLeft = containerElement.scrollLeft;
    const maxScroll = containerElement.scrollWidth - containerElement.clientWidth;

    if (maxScroll <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(1, scrollLeft / maxScroll));
  }

  // Get the total scrollable width
  const totalWidth = containerElement.scrollWidth;

  // Get positions relative to viewport
  const containerRect = containerElement.getBoundingClientRect();
  const targetRect = targetElement instanceof DOMRect ? targetElement : targetElement.getBoundingClientRect();

  // Calculate target's position relative to the container's content start
  // We need to add scrollLeft because getBoundingClientRect gives viewport position
  const targetPositionRelativeToContainer = targetRect.left - containerRect.left + containerElement.scrollLeft;

  // Calculate position within total scrollable area (0 to 1)
  const scrollRatio = targetPositionRelativeToContainer / totalWidth;
  return Math.max(0, Math.min(1, scrollRatio));
}

export function getChunkScrollDepthInMobileForHorizontalPagination(currentChunkElement: HTMLElement | undefined, serializedPositionElement?: HTMLElement | Range | DOMRect) {
  return calculateHorizontalScrollPercentOfElement({
    containerElement: currentChunkElement,
    targetElement: serializedPositionElement,
  });
}

function getTotalWordsInDocument(documentChunks: WebviewDocumentChunk[]) {
  return sumBy(documentChunks, (chunk) => chunk.word_count ?? 0);
}

function getWordsUntilCurrentChunk(documentChunks: WebviewDocumentChunk[], currentChunkIndex: number) {
  return sumBy(documentChunks.slice(0, currentChunkIndex), (chunk) => chunk.word_count ?? 0);
}

type DocumentReadingProgress = {
  wordsScrolled: number;
  relativeProgress: number;
};

export function calculateDocumentReadingProgress(
  documentChunks: WebviewDocumentChunk[],
  currentChunkIndex: number,
  relativeProgressInChunk: number,
): DocumentReadingProgress {
  if (documentChunks.length === 0) {
    return { wordsScrolled: 0, relativeProgress: 0 };
  }

  if (documentChunks.length === 1) {
    const totalWords = documentChunks[0].word_count ?? 0;
    return {
      wordsScrolled: totalWords * relativeProgressInChunk,
      relativeProgress: relativeProgressInChunk,
    };
  }

  const totalWordsInDocument = getTotalWordsInDocument(documentChunks);
  if (totalWordsInDocument === 0) {
    return { wordsScrolled: 0, relativeProgress: 0 };
  }

  const wordsUntilCurrentChunk = getWordsUntilCurrentChunk(documentChunks, currentChunkIndex);
  const currentChunk = documentChunks[currentChunkIndex];
  const relativeWordsInCurrentChunk = relativeProgressInChunk * (currentChunk?.word_count ?? 0);
  const totalWordsScrolled = wordsUntilCurrentChunk + relativeWordsInCurrentChunk;

  return {
    wordsScrolled: totalWordsScrolled,
    relativeProgress: Number(totalWordsScrolled / totalWordsInDocument),
  };
}

// Should return the relative progress in the specific chunk
// essentially the reverse of calculateDocumentReadingProgress
export function calculateRelativeChunkScrollDepthFromTotalScrollDepth(
  documentChunks: WebviewDocumentChunk[],
  absoluteScrollDepth: number,
): {chunkIndex: number; scrollDepthWithinChunk: number;} {
  if (documentChunks.length === 0) {
    return { chunkIndex: 0, scrollDepthWithinChunk: 0 };
  }

  if (documentChunks.length === 1) {
    return { chunkIndex: 0, scrollDepthWithinChunk: absoluteScrollDepth };
  }
  const totalWordsInDocument = getTotalWordsInDocument(documentChunks);
  if (totalWordsInDocument === 0) {
    return { chunkIndex: 0, scrollDepthWithinChunk: 0 };
  }
  // for each chunk, figure out their total depth if they were read fully
  let currentTotalScrollDepthOfChunks = 0;
  for (let i = 0; i < documentChunks.length; i++) {
    const currentChunk = documentChunks[i];
    const wordCount = currentChunk.word_count ?? 0;
    const totalScrollDepthOfChunk = wordCount / totalWordsInDocument;
    // This value represents the total absolute scroll depth up to the end of the chunk
    const totalAbsoluteScrollDepthOfChunk = totalScrollDepthOfChunk + currentTotalScrollDepthOfChunks;
    if (totalAbsoluteScrollDepthOfChunk > absoluteScrollDepth) {
      const amountOfScrollDepthTakenByCurrentChunk = absoluteScrollDepth - currentTotalScrollDepthOfChunks;
      const relativeScrollDepthWithinChunk = amountOfScrollDepthTakenByCurrentChunk / totalScrollDepthOfChunk;
      return { chunkIndex: i, scrollDepthWithinChunk: Math.max(0, Math.min(1, relativeScrollDepthWithinChunk)) };
    }
    currentTotalScrollDepthOfChunks += totalScrollDepthOfChunk;
  }
  return { chunkIndex: documentChunks.length - 1, scrollDepthWithinChunk: 1 };
}
